import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Divider,
} from '@mui/material';
import AppShell from '../components/layout/AppShell';
import PageHeader from '../components/layout/PageHeader';
import DataTable from '../components/ui/DataTable';
import RightDrawer from '../components/ui/RightDrawer';
import StatusPill from '../components/ui/StatusPill';
import EmptyState from '../components/ui/EmptyState';
import { Search } from '../components/ui/Icons';
import CategoryPills from '../components/AdminMenuV2/CategoryPills';
import MenuStatsRow from '../components/AdminMenuV2/MenuStatsRow';
import CategoriesTab from '../components/AdminMenuV2/CategoriesTab';
import MenuItemImageCell from '../components/AdminMenuV2/MenuItemImageCell';
import MenuItemActionsMenu from '../components/AdminMenuV2/MenuItemActionsMenu';
import SizesEditor from '../components/AdminMenuV2/SizesEditor';
import AddOnsChecklist from '../components/AdminMenuV2/AddOnsChecklist';
import { useAuth } from '../context/AuthContext';
import useMenuItemForm from '../hooks/useMenuItemForm';
import { toast } from '../lib/toast';
import { formatCurrency } from '../utils/formatCurrency';
import {
  getMenuItemsAll,
  getRestaurant,
  addMenuItem,
  updateMenuItem,
  deleteMenuItem,
  updateItemAvailability,
  getMenuItemAddons,
  createAddon,
  updateAddon,
  deleteAddon,
  updateSectionOrder,
  renameSection,
  deleteSection,
  getCurrencies,
  updateRestaurant,
  getMenuItemTranslations,
  saveMenuItemTranslations,
  getSectionTranslations,
  saveSectionTranslations,
  translateMenuItem,
  API_ORIGIN,
} from '../services/api';
import AddonManagementModal from '../components/Admin/AddonManagementModal';
import AddonForm from '../components/AddonForm';
import MenuSettingsPanel from '../components/AdminMenuV2/MenuSettingsPanel';

// Languages offered in the Edit drawer's name/description tabs. English is the
// base (stored on menu_items); es/ar live in menu_item_translations. `rtl` drives
// per-field text direction so the Arabic value reads right-to-left regardless of
// the surrounding UI language.
const LANGS = [
  { code: 'en', nativeName: 'English', flag: '🇬🇧' },
  { code: 'es', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'ar', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
];

const TABS = [
  { id: 'items', labelKey: 'menuV2.tabs.menuItems', fallback: 'Menu Items' },
  { id: 'categories', labelKey: 'menuV2.tabs.categories', fallback: 'Categories' },
  { id: 'addons', labelKey: 'menuV2.tabs.addOns', fallback: 'Add-Ons' },
  { id: 'modifiers', labelKey: 'menuV2.tabs.modifiers', fallback: 'Modifiers' },
  { id: 'settings', labelKey: 'menuV2.tabs.settings', fallback: 'Menu Settings' },
];

const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) return imageUrl;
  return `${API_ORIGIN}${imageUrl}`;
};

/**
 * Labelled group inside the Edit drawer: an overline heading + its fields,
 * separated from the previous group by a top border (omitted on the first).
 */
const DrawerSection = ({ title, action, first, children }) => (
  <Box sx={{ pt: first ? 0 : 2.5, borderTop: first ? 0 : '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'text.secondary',
        }}
      >
        {title}
      </Typography>
      {action}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
  </Box>
);

const AdminMenuV2 = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const { restaurants, isManager, isVerified, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();

  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantCurrency, setRestaurantCurrency] = useState({ code: 'USD', symbol: '$', position: 'before' });
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState('items');
  const [activeCategory, setActiveCategory] = useState('__all__');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('category_asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [editAddonsLoading, setEditAddonsLoading] = useState(false);
  const [editAddons, setEditAddons] = useState([]);
  // When true, the Edit drawer renders in "create" mode (no editingItem yet).
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availabilityTogglingId, setAvailabilityTogglingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Categories tab: `section_order` is the ordered source of truth (a category
  // in v2 == the menu item `section`). Sections without items can still live in
  // the order, so we render rows from `sectionOrder`, not the derived list.
  const [sectionOrder, setSectionOrder] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [categoryDeleteName, setCategoryDeleteName] = useState(null);
  const [categoryBusy, setCategoryBusy] = useState(false);

  // Category (section) name translations. `sectionTrans` is a map keyed by base
  // section name → { es: { name, isAutoTranslated }, ar: {...} }; the dialog edits
  // one category at a time.
  const [sectionTrans, setSectionTrans] = useState({});
  const [translateTarget, setTranslateTarget] = useState(null);
  const [dialogTrans, setDialogTrans] = useState({
    es: { name: '', isAutoTranslated: false },
    ar: { name: '', isAutoTranslated: false },
  });
  const [translateBusy, setTranslateBusy] = useState(false);
  const [translateTranslating, setTranslateTranslating] = useState(false);

  // Addon management modal state (opens in-place over the drawer when the user
  // clicks "Manage" next to the add-ons list).
  const [addonModalItem, setAddonModalItem] = useState(null);
  const [addonsList, setAddonsList] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [addonFormOpen, setAddonFormOpen] = useState(false);
  const [addonFormEditing, setAddonFormEditing] = useState(null);
  const [addonDeleteConfirmId, setAddonDeleteConfirmId] = useState(null);

  // Settings tab: currency / tax / OTP plus restaurant identity. Mirrors the
  // classic Admin.js settings state and reuses the same updateRestaurant payload.
  const [currencies, setCurrencies] = useState([]);
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantSlug, setRestaurantSlug] = useState('');
  const [loadedSlug, setLoadedSlug] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [taxConfig, setTaxConfig] = useState({
    tax_enabled: false,
    tax_rate: 0,
    tax_inclusive: true,
    tax_label: 'VAT',
    tax_number: '',
  });
  const [requireCustomerOtp, setRequireCustomerOtp] = useState(false);

  const {
    editingItem,
    editItemName, setEditItemName,
    editItemDescription, setEditItemDescription,
    editItemPrice, setEditItemPrice,
    editItemCategory,
    editItemSection, setEditItemSection,
    editImagePreview, setEditImagePreview,
    editItemIsPopular, setEditItemIsPopular,
    editItemSizes, setEditItemSizes,
    editTranslations, setEditTranslations,
    editActiveLangTab, setEditActiveLangTab,
    editTranslating, setEditTranslating,
    updateEditTranslation,
    setEditTranslationsFromArray,
    populateEditForm,
    resetEditForm,
    handleEditImageChange,
  } = useMenuItemForm();

  // Resolve restaurant from URL param (admin) or AuthContext (manager).
  useEffect(() => {
    const restaurantParam = searchParams.get('restaurant');
    if (restaurantParam) {
      const paramId = parseInt(restaurantParam, 10);
      if (Number.isFinite(paramId)) {
        setRestaurantId(paramId);
        return;
      }
    }
    if (restaurants?.length > 0 && !restaurantId) {
      const preferred = restaurants.find(r => r.id !== 1) || restaurants[0];
      setRestaurantId(preferred.id);
    }
  }, [restaurants, searchParams, restaurantId]);

  // Fetch items + restaurant meta whenever restaurant changes.
  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [restRes, menuRes] = await Promise.all([
          getRestaurant(restaurantId).catch(() => null),
          getMenuItemsAll(restaurantId),
        ]);
        if (cancelled) return;
        if (restRes?.data) {
          setRestaurantName(restRes.data.name || '');
          setRestaurantSlug(restRes.data.slug || '');
          setLoadedSlug(restRes.data.slug || '');
          if (restRes.data.currency) setRestaurantCurrency(restRes.data.currency);
          setTaxConfig({
            tax_enabled: Boolean(restRes.data.tax_enabled),
            tax_rate: parseFloat(restRes.data.tax_rate) || 0,
            tax_inclusive: restRes.data.tax_inclusive == null ? true : Boolean(restRes.data.tax_inclusive),
            tax_label: restRes.data.tax_label || 'VAT',
            tax_number: restRes.data.tax_number || '',
          });
          setRequireCustomerOtp(Boolean(restRes.data.require_customer_otp));
        }
        const items = Array.isArray(menuRes.data) ? menuRes.data : [];
        setMenuItems(items);

        // Section order: seed from the restaurant, then append any sections that
        // exist on items but aren't in the saved order (mirrors Admin.js). Persist
        // the merge so the two stay in sync; a persist hiccup must not break load.
        let order = Array.isArray(restRes?.data?.section_order) ? restRes.data.section_order : [];
        const itemSections = [...new Set(items.map((i) => i.section).filter(Boolean))];
        const missing = itemSections.filter((s) => !order.includes(s));
        if (missing.length) {
          order = [...order, ...missing.sort()];
          setSectionOrder(order);
          try {
            await updateSectionOrder(restaurantId, order);
          } catch (persistErr) {
            // Non-fatal: the order is correct in-memory; persistence retries on next mutation.
          }
        } else {
          setSectionOrder(order);
        }
      } catch (err) {
        if (!cancelled) toast.error(t('menuV2.errors.loadFailed', 'Failed to load menu'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [restaurantId, t]);

  // Load the currency catalogue once for the Settings tab dropdown.
  useEffect(() => {
    getCurrencies()
      .then((res) => { if (res?.data?.currencies) setCurrencies(res.data.currencies); })
      .catch(() => setCurrencies([]));
  }, []);

  // Fetch category (section) name translations → map keyed by base name. Used for
  // the Categories tab indicator and to seed the per-category translation dialog.
  const refreshSectionTranslations = async (rid = restaurantId) => {
    if (!rid) return;
    try {
      const res = await getSectionTranslations(rid);
      const map = {};
      for (const row of res?.data || []) {
        if (!map[row.section_name]) map[row.section_name] = {};
        map[row.section_name][row.language_code] = {
          name: row.translated_name || '',
          isAutoTranslated: !!row.is_auto_translated,
        };
      }
      setSectionTrans(map);
    } catch (err) {
      // non-fatal — indicators/dialog just start empty
    }
  };

  useEffect(() => {
    if (restaurantId) refreshSectionTranslations(restaurantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  // Save currency / tax / OTP (and name + slug). Mirrors Admin.js's
  // handleSaveRestaurantSettings — same payload, same updateRestaurant contract.
  const handleSaveSettings = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!restaurantId) return;
    setSavingSettings(true);
    try {
      const payload = {
        name: restaurantName,
        currency_code: restaurantCurrency.code,
        currency_symbol: restaurantCurrency.symbol,
        currency_position: restaurantCurrency.position,
        tax_enabled: taxConfig.tax_enabled,
        tax_rate: taxConfig.tax_rate,
        tax_inclusive: taxConfig.tax_inclusive,
        tax_label: taxConfig.tax_label,
        tax_number: taxConfig.tax_number,
        require_customer_otp: requireCustomerOtp,
      };
      // Only send `slug` when an admin actually changed it (backend ignores it
      // for non-admins). Avoids re-validating an unchanged slug.
      const trimmedSlug = restaurantSlug.trim().toLowerCase();
      if (isAdmin() && trimmedSlug && trimmedSlug !== loadedSlug) {
        payload.slug = trimmedSlug;
      }
      const res = await updateRestaurant(restaurantId, payload);
      if (res?.data?.slug) {
        setRestaurantSlug(res.data.slug);
        setLoadedSlug(res.data.slug);
      }
      toast.success(t('admin.currency.updated', {
        code: restaurantCurrency.code,
        symbol: restaurantCurrency.symbol,
      }));
    } catch (err) {
      toast.error(err.response?.data?.error || t('menuV2.errors.saveFailed', 'Failed to save'));
    } finally {
      setSavingSettings(false);
    }
  };

  // Localized display helpers for the active UI language. These affect ONLY what
  // is rendered — the base `name`/`section` stay the source of truth for editing,
  // filtering, and section rename/delete/reorder (which key by the base string).
  const localizeName = (item) =>
    (lang === 'es' ? item.name_es : lang === 'ar' ? item.name_ar : null) || item.name;
  const localizeDescription = (item) =>
    (lang === 'es' ? item.description_es : lang === 'ar' ? item.description_ar : null) || item.description;
  const sectionLabel = (base) => {
    if (!base) return base;
    const tr = sectionTrans[base];
    return (lang === 'es' ? tr?.es?.name : lang === 'ar' ? tr?.ar?.name : null) || base;
  };

  // Derived: distinct categories (use `section` as the category source — that's
  // how the existing schema groups items). `id` stays the base section name (the
  // value persisted on items); only `label` is localized for display.
  const categories = useMemo(() => {
    const counts = new Map();
    for (const item of menuItems) {
      const key = item.section || 'Other';
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return [...counts.entries()].map(([id, count]) => ({ id, label: sectionLabel(id), count }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems, sectionTrans, lang]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    let items = menuItems.filter(item => {
      if (activeCategory !== '__all__' && (item.section || 'Other') !== activeCategory) return false;
      // Match against both the base and the localized name/description so search
      // works whichever language the admin is typing in.
      const haystack = `${item.name || ''} ${item.description || ''} ${localizeName(item) || ''} ${localizeDescription(item) || ''}`.toLowerCase();
      if (term && !haystack.includes(term)) return false;
      return true;
    });
    const byName = (a, b) => String(localizeName(a) || '').localeCompare(String(localizeName(b) || ''), lang);
    const cmp = {
      category_asc: (a, b) => String(sectionLabel(a.section) || '').localeCompare(String(sectionLabel(b.section) || ''), lang) || byName(a, b),
      name_asc: byName,
      name_desc: (a, b) => byName(b, a),
      price_asc: (a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0),
      price_desc: (a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0),
    }[sortBy] || ((a, b) => 0);
    return [...items].sort(cmp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems, activeCategory, search, sortBy, sectionTrans, lang]);

  const pagedItems = useMemo(() => {
    const start = page * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  useEffect(() => { setPage(0); }, [activeCategory, search, sortBy, pageSize]);

  // Open the Edit drawer in "create" mode: a blank form, no editingItem.
  const openCreateDrawer = () => {
    resetEditForm();
    setEditAddons([]);
    setCreating(true);
  };

  // Reset the drawer on every close path (cancel, backdrop, post-save) so the
  // create flag never leaks into the next open.
  const closeDrawer = () => {
    resetEditForm();
    setCreating(false);
    setEditAddons([]);
  };

  // Fetch the item's add-ons + saved translations when the drawer opens.
  const handleEditItem = async (item) => {
    setCreating(false);
    populateEditForm(item);
    setEditImagePreview(item.image_url ? getImageUrl(item.image_url) : null);

    // Hydrate the es/ar language tabs from saved translations. Non-fatal: if the
    // request fails the tabs simply stay empty.
    try {
      const res = await getMenuItemTranslations(item.id);
      setEditTranslationsFromArray(res?.data || []);
    } catch (err) {
      // ignore — leave translation tabs empty
    }

    setEditAddons([]);
    setEditAddonsLoading(true);
    try {
      const res = await getMenuItemAddons(item.id);
      setEditAddons(shapeAddons(res.data));
    } catch (err) {
      setEditAddons([]);
    } finally {
      setEditAddonsLoading(false);
    }
  };

  // Auto-fill the es/ar tabs from the English name/description via the translate
  // API. Mirrors Admin.js's handleEditAutoTranslate; merges results into the edit
  // translation state without clobbering the other language.
  const handleEditAutoTranslate = async () => {
    if (!editItemName.trim()) {
      toast.error(t('translation.enterBaseFirst', 'Enter the English name first.'));
      return;
    }
    setEditTranslating(true);
    try {
      const res = await translateMenuItem({
        name: editItemName,
        description: editItemDescription || '',
        sourceLang: 'en',
        targetLangs: ['es', 'ar'],
      });
      const trans = res?.data?.translations;
      if (!trans || typeof trans !== 'object') {
        toast.error(t('translation.failed', 'Translation failed'));
        return;
      }
      const updates = {};
      const errors = [];
      for (const [code, tr] of Object.entries(trans)) {
        if (code === 'en') continue;
        if (tr && tr.error) { errors.push(tr.error); continue; }
        const name = (tr && tr.name) || '';
        const description = (tr && tr.description) || '';
        if (name || description) {
          updates[code] = { name, description, isAutoTranslated: true };
        }
      }
      if (Object.keys(updates).length > 0) {
        setEditTranslations(prev => ({ ...prev, ...updates }));
        toast.success(t('translation.success', 'Translations generated'));
      } else if (errors.some(e => String(e).includes('API_KEY_REQUIRED'))) {
        toast.error(t('translation.apiKeyRequired', 'Translation API key is required.'));
      } else {
        toast.error(t('translation.failed', 'Translation failed'));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('translation.failed', 'Translation failed'));
    } finally {
      setEditTranslating(false);
    }
  };

  // Shape addon-category rows from the backend into the {id, categoryName, options:[{...}]}
  // tree the drawer's checklist and AddonManagementModal both expect.
  const shapeAddons = (rows) => (rows || []).map((cat) => ({
    id: cat.id,
    categoryName: cat.category_name || cat.categoryName,
    categoryNameEs: cat.category_name_es ?? cat.categoryNameEs ?? '',
    categoryNameAr: cat.category_name_ar ?? cat.categoryNameAr ?? '',
    isRequired: !!(cat.is_required ?? cat.isRequired),
    allowMultiple: !!(cat.allow_multiple ?? cat.allowMultiple),
    options: (cat.options || cat.addon_options || []).map((opt) => ({
      id: opt.id,
      name: opt.option_name || opt.name,
      nameEs: opt.option_name_es ?? opt.nameEs ?? '',
      nameAr: opt.option_name_ar ?? opt.nameAr ?? '',
      price: parseFloat(opt.additional_price ?? opt.price) || 0,
      isDefault: !!(opt.is_default ?? opt.isDefault),
    })),
  }));

  const refreshAddonsForItem = async (itemId) => {
    const res = await getMenuItemAddons(itemId);
    const shaped = shapeAddons(res.data);
    setAddonsList(shaped);
    // Also keep the drawer's checklist in sync if the user is editing the same item.
    if (editingItem && editingItem.id === itemId) {
      setEditAddons(shaped);
    }
    return shaped;
  };

  const openAddonManagement = async (item) => {
    setAddonModalItem(item);
    setAddonsList([]);
    setAddonFormOpen(false);
    setAddonFormEditing(null);
    setAddonsLoading(true);
    try {
      await refreshAddonsForItem(item.id);
    } catch (err) {
      toast.error(err.response?.data?.error || t('menuV2.errors.loadFailed', 'Failed to load menu'));
      setAddonsList([]);
    } finally {
      setAddonsLoading(false);
    }
  };

  const closeAddonManagement = () => {
    setAddonModalItem(null);
    setAddonsList([]);
    setAddonFormOpen(false);
    setAddonFormEditing(null);
    setAddonDeleteConfirmId(null);
  };

  const handleSaveAddonCategory = async (data) => {
    if (!addonModalItem) return;
    try {
      if (addonFormEditing) {
        await updateAddon(addonFormEditing.id, {
          categoryName: data.categoryName,
          categoryNameEs: data.categoryNameEs,
          categoryNameAr: data.categoryNameAr,
          isRequired: data.isRequired,
          allowMultiple: data.allowMultiple,
          options: data.options,
        });
        toast.success(t('admin.addons.updated', 'Add-on updated'));
      } else {
        await createAddon(addonModalItem.id, data);
        toast.success(t('admin.addons.created', 'Add-on created'));
      }
      await refreshAddonsForItem(addonModalItem.id);
      setAddonFormOpen(false);
      setAddonFormEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.error || t('menuV2.errors.saveFailed', 'Failed to save'));
    }
  };

  const handleDeleteAddonConfirm = async () => {
    if (addonDeleteConfirmId == null || !addonModalItem) return;
    const id = addonDeleteConfirmId;
    setAddonDeleteConfirmId(null);
    try {
      await deleteAddon(id);
      await refreshAddonsForItem(addonModalItem.id);
      toast.success(t('admin.addons.deleted', 'Add-on deleted'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('menuV2.errors.deleteFailed', 'Failed to delete'));
    }
  };

  const handleSave = async () => {
    if (!editItemName.trim() || !editItemPrice) {
      toast.error(t('menuV2.errors.nameAndPrice', 'Name and price are required.'));
      return;
    }
    setSaving(true);
    try {
      const sizesPayload = editItemSizes.map((s, idx) => ({
        size_name: s.size_name,
        size_name_es: s.size_name_es || null,
        size_name_ar: s.size_name_ar || null,
        price: parseFloat(s.price),
        is_default: !!s.is_default,
        display_order: Number.isFinite(s.display_order) ? s.display_order : idx,
      }));

      // Non-empty es/ar translations to persist. English lives in menu_items.name,
      // so it's saved by the create/update call below — skip it here.
      const toSave = {};
      for (const [code, tr] of Object.entries(editTranslations)) {
        if (code === 'en') continue;
        if (tr.name) toSave[code] = tr;
      }

      let targetId;
      if (creating) {
        // The create endpoint only inserts the base columns (name/description/
        // price/section/category/image/display_order). Create first, then patch
        // is_popular/sizes via updateMenuItem if either was set in the drawer.
        const display_order = menuItems.filter(
          (i) => (i.section || '') === (editItemSection || '')
        ).length;
        const createPayload = {
          name: editItemName.trim(),
          description: editItemDescription || '',
          price: parseFloat(editItemPrice),
          section: editItemSection || null,
          category: editItemCategory || null,
          display_order,
        };
        if (editImagePreview && editImagePreview.startsWith('data:')) {
          createPayload.imageData = editImagePreview;
        }
        const res = await addMenuItem(restaurantId, createPayload);
        targetId = res?.data?.id;
        if (targetId && (editItemIsPopular || sizesPayload.length)) {
          await updateMenuItem(targetId, {
            is_popular: editItemIsPopular,
            sizes: sizesPayload,
          });
        }
      } else {
        targetId = editingItem.id;
        const payload = {
          name: editItemName.trim(),
          description: editItemDescription || '',
          price: parseFloat(editItemPrice),
          section: editItemSection || null,
          category: editItemCategory || null,
          is_popular: editItemIsPopular,
          sizes: sizesPayload,
        };
        if (editImagePreview && editImagePreview.startsWith('data:')) {
          payload.imageData = editImagePreview;
        }
        await updateMenuItem(targetId, payload);
      }

      if (targetId && Object.keys(toSave).length) {
        try {
          await saveMenuItemTranslations(targetId, toSave);
        } catch (e) {
          toast.error(t('translation.failed', 'Failed to save translations'));
        }
      }

      toast.success(
        creating
          ? t('menuV2.success.created', 'Item added')
          : t('menuV2.success.saved', 'Item updated')
      );
      const refreshed = await getMenuItemsAll(restaurantId);
      setMenuItems(Array.isArray(refreshed.data) ? refreshed.data : []);
      closeDrawer();
    } catch (err) {
      toast.error(err.response?.data?.error || t('menuV2.errors.saveFailed', 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAvailability = async (item) => {
    const next = !(item.is_available === 1 || item.is_available === true);
    setAvailabilityTogglingId(item.id);
    try {
      await updateItemAvailability(item.id, { isAvailable: next });
      setMenuItems(prev => prev.map(i => i.id === item.id ? { ...i, is_available: next ? 1 : 0 } : i));
    } catch (err) {
      toast.error(err.response?.data?.error || t('menuV2.errors.toggleFailed', 'Failed to update availability'));
    } finally {
      setAvailabilityTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmId == null) return;
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    try {
      await deleteMenuItem(id);
      setMenuItems(prev => prev.filter(i => i.id !== id));
      toast.success(t('menuV2.success.deleted', 'Item deleted'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('menuV2.errors.deleteFailed', 'Failed to delete'));
    }
  };

  // --- Categories (menu sections) ------------------------------------------

  // Re-fetch items after a section mutation so counts + category pills stay
  // correct, merging any newly-introduced sections into the order.
  const refreshMenuItems = async () => {
    const res = await getMenuItemsAll(restaurantId);
    const items = Array.isArray(res.data) ? res.data : [];
    setMenuItems(items);
    const itemSections = [...new Set(items.map((i) => i.section).filter(Boolean))];
    setSectionOrder((prev) => {
      const missing = itemSections.filter((s) => !prev.includes(s));
      return missing.length ? [...prev, ...missing.sort()] : prev;
    });
  };

  const handleAddCategory = async () => {
    const name = (newCategoryName || '').trim();
    if (!name) {
      toast.error(t('admin.renameSection.validation.required', 'Please enter a new category name'));
      return;
    }
    if (sectionOrder.includes(name)) {
      toast.error(t('validation.sectionExists', 'This category already exists'));
      return;
    }
    const prev = sectionOrder;
    const newOrder = [...sectionOrder, name];
    setSectionOrder(newOrder);
    setCategoryBusy(true);
    try {
      await updateSectionOrder(restaurantId, newOrder);
      setNewCategoryName('');
      toast.success(t('admin.sections.addedSuccess', { name }));
    } catch (err) {
      setSectionOrder(prev);
      toast.error(err.response?.data?.error || t('menuV2.errors.saveFailed', 'Failed to save'));
    } finally {
      setCategoryBusy(false);
    }
  };

  const moveCategory = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= sectionOrder.length) return;
    const prev = sectionOrder;
    const newOrder = [...sectionOrder];
    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
    setSectionOrder(newOrder);
    setCategoryBusy(true);
    try {
      await updateSectionOrder(restaurantId, newOrder);
      toast.success(t('admin.sections.orderUpdated', 'Category order updated!'));
    } catch (err) {
      setSectionOrder(prev);
      toast.error(err.response?.data?.error || t('menuV2.errors.saveFailed', 'Failed to save'));
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleRenameCategoryConfirm = async () => {
    const oldName = renameTarget;
    const newName = (renameValue || '').trim();
    if (!oldName) return;
    if (!newName) {
      toast.error(t('admin.renameSection.validation.required', 'Please enter a new category name'));
      return;
    }
    if (newName === oldName) {
      setRenameTarget(null);
      return;
    }
    if (sectionOrder.includes(newName)) {
      toast.error(t('validation.sectionExists', 'This category already exists'));
      return;
    }
    setCategoryBusy(true);
    try {
      await renameSection(restaurantId, oldName, newName);
      const newOrder = sectionOrder.map((s) => (s === oldName ? newName : s));
      setSectionOrder(newOrder);
      await updateSectionOrder(restaurantId, newOrder);
      await refreshMenuItems();
      if (activeCategory === oldName) setActiveCategory('__all__');
      setRenameTarget(null);
      setRenameValue('');
      await refreshSectionTranslations();
      toast.success(t('admin.sections.renamedSuccess', 'Category renamed'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('api.errors.renameSection', 'Failed to rename category'));
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleDeleteCategoryConfirm = async () => {
    const name = categoryDeleteName;
    if (!name) return;
    setCategoryBusy(true);
    try {
      await deleteSection(restaurantId, name);
      const newOrder = sectionOrder.filter((s) => s !== name);
      setSectionOrder(newOrder);
      await updateSectionOrder(restaurantId, newOrder);
      await refreshMenuItems();
      if (activeCategory === name) setActiveCategory('__all__');
      setCategoryDeleteName(null);
      await refreshSectionTranslations();
      toast.success(t('admin.sections.deletedSuccess', 'Category removed (items moved to Other)'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('api.errors.deleteSection', 'Failed to delete category'));
    } finally {
      setCategoryBusy(false);
    }
  };

  // --- Category (section) name translations -------------------------------

  const openTranslateDialog = (name) => {
    const existing = sectionTrans[name] || {};
    setDialogTrans({
      es: { name: existing.es?.name || '', isAutoTranslated: !!existing.es?.isAutoTranslated },
      ar: { name: existing.ar?.name || '', isAutoTranslated: !!existing.ar?.isAutoTranslated },
    });
    setTranslateTarget(name);
  };

  const updateDialogTrans = (lang, value) => {
    setDialogTrans((prev) => ({
      ...prev,
      [lang]: { name: value, isAutoTranslated: false },
    }));
  };

  // Auto-fill es/ar from the English category name via the translate API.
  const handleSectionAutoTranslate = async () => {
    if (!translateTarget) return;
    setTranslateTranslating(true);
    try {
      const res = await translateMenuItem({
        name: translateTarget,
        description: '',
        sourceLang: 'en',
        targetLangs: ['es', 'ar'],
      });
      const trans = res?.data?.translations;
      if (!trans || typeof trans !== 'object') {
        toast.error(t('translation.failed', 'Translation failed'));
        return;
      }
      const next = { ...dialogTrans };
      let any = false;
      const errors = [];
      for (const lang of ['es', 'ar']) {
        const tr = trans[lang];
        if (tr && tr.error) { errors.push(tr.error); continue; }
        const name = (tr && tr.name) || '';
        if (name) { next[lang] = { name, isAutoTranslated: true }; any = true; }
      }
      if (any) {
        setDialogTrans(next);
        toast.success(t('translation.success', 'Translations generated'));
      } else if (errors.some((e) => String(e).includes('API_KEY_REQUIRED'))) {
        toast.error(t('translation.apiKeyRequired', 'Translation API key is required.'));
      } else {
        toast.error(t('translation.failed', 'Translation failed'));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('translation.failed', 'Translation failed'));
    } finally {
      setTranslateTranslating(false);
    }
  };

  const handleSaveSectionTranslations = async () => {
    if (!translateTarget) return;
    setTranslateBusy(true);
    try {
      await saveSectionTranslations(restaurantId, translateTarget, dialogTrans);
      await refreshSectionTranslations();
      setTranslateTarget(null);
      toast.success(t('menuV2.categories.translationsSaved', 'Category translations saved'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('menuV2.errors.saveFailed', 'Failed to save'));
    } finally {
      setTranslateBusy(false);
    }
  };

  const columns = [
    {
      id: 'item',
      header: t('menuV2.table.item', 'Item'),
      render: (row) => (
        <MenuItemImageCell
          item={row}
          name={localizeName(row)}
          description={localizeDescription(row)}
          getImageUrl={getImageUrl}
        />
      ),
    },
    {
      id: 'category',
      header: t('menuV2.table.category', 'Category'),
      hideOnSm: true,
      render: (row) => (row.section ? <StatusPill status="info" label={sectionLabel(row.section)} /> : '—'),
    },
    {
      id: 'price',
      header: `${t('menuV2.table.price', 'Price')} (${restaurantCurrency.code})`,
      align: 'start',
      render: (row) => (
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {formatCurrency(row.price, restaurantCurrency)}
        </Typography>
      ),
    },
    {
      id: 'status',
      header: t('menuV2.table.status', 'Status'),
      align: 'center',
      // Stop propagation so toggling availability doesn't also open the edit drawer.
      render: (row) => (
        <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
          <Switch
            size="small"
            checked={!!(row.is_available === 1 || row.is_available === true)}
            onChange={() => handleToggleAvailability(row)}
            disabled={availabilityTogglingId === row.id}
            inputProps={{ 'aria-label': t('menuV2.table.statusToggle', 'Toggle availability') }}
          />
        </Box>
      ),
    },
    {
      id: 'popular',
      header: t('menuV2.table.popular', 'Popular'),
      hideOnSm: true,
      render: (row) => (
        row.is_popular ? (
          <StatusPill tone="trial" label={`⭐ ${t('menuV2.badge.topSeller', 'Top Seller')}`} />
        ) : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>—</Typography>
        )
      ),
    },
    {
      id: 'actions',
      header: t('menuV2.table.actions', 'Actions'),
      align: 'end',
      width: 120,
      render: (row) => (
        <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
          <MenuItemActionsMenu
            item={row}
            onEdit={handleEditItem}
            onManageAddons={openAddonManagement}
            onDelete={(id) => setDeleteConfirmId(id)}
          />
        </Box>
      ),
    },
  ];

  // Unverified managers see the gated view (mirrors Admin.js behavior).
  if (isManager() && !isVerified()) {
    return (
      <AppShell maxWidth={false}>
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {t('admin.pendingVerification.title', 'Verification pending')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('admin.pendingVerification.message')}
          </Typography>
          <Button component={Link} to="/menu/9?mode=demo" variant="contained">
            {t('admin.pendingVerification.viewDemo', 'View demo menu')}
          </Button>
        </Box>
      </AppShell>
    );
  }

  const pillItems = [
    { id: '__all__', label: t('menuV2.pills.allItems', 'All Items'), count: menuItems.length },
    ...categories,
  ];

  // Active language for the Edit drawer's name/description tabs. English is the
  // base (editItemName/editItemDescription); es/ar read from editTranslations.
  const activeLang = LANGS.find(l => l.code === editActiveLangTab) || LANGS[0];
  const isBaseLang = activeLang.code === 'en';
  const activeName = isBaseLang
    ? editItemName
    : (editTranslations[activeLang.code]?.name || '');
  const activeDescription = isBaseLang
    ? editItemDescription
    : (editTranslations[activeLang.code]?.description || '');
  const setActiveName = (value) => {
    if (isBaseLang) setEditItemName(value);
    else updateEditTranslation(activeLang.code, 'name', value);
  };
  const setActiveDescription = (value) => {
    if (isBaseLang) setEditItemDescription(value);
    else updateEditTranslation(activeLang.code, 'description', value);
  };
  // Per-field RTL: an Arabic value always reads right-to-left, independent of the
  // surrounding UI language. (This is field-level direction, not the app-wide
  // dir cascade — it does not bypass the language-driven flip in App.js.)
  const fieldInputProps = {
    dir: activeLang.rtl ? 'rtl' : 'ltr',
    style: { textAlign: activeLang.rtl ? 'right' : 'left' },
  };

  return (
    <AppShell maxWidth={false}>
      <PageHeader
        title={t('menuV2.title', 'Menu Management')}
        subtitle={t('menuV2.subtitle', 'Create, update, and organize your menu items, categories, and add-ons.')}
        action={
          tab === 'items' ? (
            <Button variant="contained" onClick={openCreateDrawer}>
              + {t('menuV2.addItem', 'Add Item')}
            </Button>
          ) : null
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map((entry) => (
            <Tab
              key={entry.id}
              value={entry.id}
              label={t(entry.labelKey, entry.fallback)}
              disabled={entry.id === 'modifiers'}
            />
          ))}
        </Tabs>
      </Box>

      {tab === 'items' && (
        <>
          <MenuStatsRow menuItems={menuItems} sectionOrder={sectionOrder} />

          <Box
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                placeholder={t('menuV2.toolbar.searchPlaceholder', 'Search menu items...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 240, maxWidth: 360 }}
              />
              <Box sx={{ flex: 1 }} />
              <Select
                size="small"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label={t('menuV2.toolbar.sort', 'Sort')}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="category_asc">{t('menuV2.toolbar.sortCategoryAsc', 'Sort: Category (A-Z)')}</MenuItem>
                <MenuItem value="name_asc">{t('menuV2.toolbar.sortNameAsc', 'Sort: Name (A-Z)')}</MenuItem>
                <MenuItem value="name_desc">{t('menuV2.toolbar.sortNameDesc', 'Sort: Name (Z-A)')}</MenuItem>
                <MenuItem value="price_asc">{t('menuV2.toolbar.sortPriceAsc', 'Sort: Price (Low to High)')}</MenuItem>
                <MenuItem value="price_desc">{t('menuV2.toolbar.sortPriceDesc', 'Sort: Price (High to Low)')}</MenuItem>
              </Select>
            </Box>

            <Divider sx={{ my: 2 }} />

            <CategoryPills
              items={pillItems}
              value={activeCategory}
              onChange={setActiveCategory}
            />
          </Box>

          <DataTable
            columns={columns}
            rows={pagedItems}
            loading={loading}
            ariaLabel={t('menuV2.table.aria', 'Menu items')}
            onRowClick={handleEditItem}
            getRowKey={(row) => row.id}
            empty={{
              icon: '🍽️',
              title: t('menuV2.empty.title', 'No items match these filters'),
              description: t('menuV2.empty.description', 'Try clearing the search or switching to All Items.'),
            }}
            pagination={{
              page,
              pageSize,
              total: filteredItems.length,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
          />
        </>
      )}

      {tab === 'categories' && (
        <CategoriesTab
          sectionOrder={sectionOrder}
          menuItems={menuItems}
          newCategoryName={newCategoryName}
          onNewCategoryNameChange={setNewCategoryName}
          onAddCategory={handleAddCategory}
          onMoveUp={(index) => moveCategory(index, -1)}
          onMoveDown={(index) => moveCategory(index, 1)}
          onRenameClick={(name) => { setRenameTarget(name); setRenameValue(name); }}
          onDeleteClick={(name) => setCategoryDeleteName(name)}
          onTranslateClick={openTranslateDialog}
          sectionTranslations={sectionTrans}
          getLabel={sectionLabel}
          busy={categoryBusy}
        />
      )}

      {tab === 'addons' && (
        <EmptyState
          icon="🧀"
          title={t('menuV2.stub.addonsTitle', 'Edit add-ons from an item')}
          description={t('menuV2.stub.addonsDescription', 'Open an item, then "Manage Add-Ons" from its action menu.')}
        />
      )}

      {tab === 'modifiers' && (
        <EmptyState
          icon="✨"
          title={t('menuV2.stub.modifiersTitle', 'Modifiers — coming soon')}
          description={t('menuV2.stub.modifiersDescription', 'Free-text choices like "No onions, extra sauce" will land here in a future release.')}
        />
      )}

      {tab === 'settings' && (
        <MenuSettingsPanel
          restaurantName={restaurantName}
          onRestaurantNameChange={setRestaurantName}
          restaurantSlug={restaurantSlug}
          onRestaurantSlugChange={setRestaurantSlug}
          isAdmin={isAdmin()}
          currencies={currencies}
          restaurantCurrency={restaurantCurrency}
          onCurrencyChange={setRestaurantCurrency}
          taxConfig={taxConfig}
          onTaxConfigChange={setTaxConfig}
          requireCustomerOtp={requireCustomerOtp}
          onRequireCustomerOtpChange={setRequireCustomerOtp}
          saving={savingSettings}
          onSubmit={handleSaveSettings}
        />
      )}

      <RightDrawer
        open={!!editingItem || creating}
        onClose={closeDrawer}
        title={creating ? t('menuV2.add.title', 'Add Menu Item') : t('menuV2.edit.title', 'Edit Menu Item')}
        width={520}
        actions={
          <>
            <Button onClick={closeDrawer} disabled={saving}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving
                ? t('common.saving', 'Saving...')
                : creating
                  ? t('menuV2.add.save', 'Add Item')
                  : t('menuV2.edit.save', 'Save Changes')}
            </Button>
          </>
        }
      >
        {(editingItem || creating) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <DrawerSection title={t('menuV2.edit.sections.details', 'Item Details')} first>
              <Tabs
                value={editActiveLangTab}
                onChange={(_, v) => setEditActiveLangTab(v)}
                variant="fullWidth"
                sx={{ minHeight: 0, mb: 0.5, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
              >
                {LANGS.map((lang) => {
                  const filled = lang.code === 'en'
                    ? !!editItemName
                    : !!editTranslations[lang.code]?.name;
                  return (
                    <Tab
                      key={lang.code}
                      value={lang.code}
                      label={`${lang.flag} ${lang.nativeName}${filled ? '' : ' —'}`}
                    />
                  );
                })}
              </Tabs>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    color: 'text.secondary',
                  }}
                >
                  {editImagePreview ? (
                    <Box component="img" src={editImagePreview} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span aria-hidden="true">🍽️</span>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Button component="label" size="small" variant="outlined">
                    {t('menuV2.edit.changeImage', 'Change Image')}
                    <input hidden type="file" accept="image/*" onChange={handleEditImageChange} />
                  </Button>
                  <TextField
                    size="small"
                    label={`${t('menuV2.edit.itemName', 'Item Name')} (${activeLang.nativeName})`}
                    value={activeName}
                    onChange={(e) => setActiveName(e.target.value)}
                    required={isBaseLang}
                    fullWidth
                    sx={{ mt: 2 }}
                    inputProps={fieldInputProps}
                  />
                  <TextField
                    size="small"
                    select
                    label={t('menuV2.edit.category', 'Category')}
                    value={editItemSection || ''}
                    onChange={(e) => setEditItemSection(e.target.value)}
                    fullWidth
                    sx={{ mt: 1.5 }}
                  >
                    <MenuItem value="">{t('menuV2.edit.none', 'None')}</MenuItem>
                    {categories.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>

              <TextField
                label={`${t('menuV2.edit.description', 'Description')} (${activeLang.nativeName})`}
                value={activeDescription}
                onChange={(e) => setActiveDescription(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                inputProps={{ maxLength: 160, ...fieldInputProps }}
                helperText={`${(activeDescription || '').length}/160`}
              />

              <Button
                size="small"
                variant="outlined"
                onClick={handleEditAutoTranslate}
                disabled={editTranslating || !editItemName.trim()}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                {editTranslating
                  ? t('translation.translating', 'Translating...')
                  : `🔄 ${t('translation.autoTranslateOthers', 'Auto-translate others')}`}
              </Button>
            </DrawerSection>

            <DrawerSection title={t('menuV2.edit.sections.pricing', 'Pricing & Sizes')}>
              <TextField
                size="small"
                type="number"
                label={`${t('menuV2.edit.price', 'Price')} (${restaurantCurrency.code})`}
                value={editItemPrice}
                onChange={(e) => setEditItemPrice(e.target.value)}
                required
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                  {t('menuV2.edit.sizesLabel', 'Sizes')}
                </Typography>
                <SizesEditor
                  sizes={editItemSizes}
                  onChange={setEditItemSizes}
                  currency={restaurantCurrency}
                />
              </Box>
            </DrawerSection>

            <DrawerSection title={t('menuV2.edit.sections.availability', 'Availability')}>
              {/* Availability toggles immediately via API, so it only applies to an
                  existing item. New items default to available and can be toggled
                  from the table after creation. */}
              {!creating && (
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={!!(editingItem?.is_available === 1 || editingItem?.is_available === true)}
                      onChange={() => handleToggleAvailability(editingItem)}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {t('menuV2.edit.availability', 'Availability')}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {t('menuV2.edit.availabilityHint', 'Item is available on the menu')}
                      </Typography>
                    </Box>
                  }
                />
              )}
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={editItemIsPopular}
                    onChange={(e) => setEditItemIsPopular(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    ⭐ {t('menuV2.edit.markPopular', 'Mark as Top Seller')}
                  </Typography>
                }
              />
            </DrawerSection>

            <DrawerSection
              title={t('menuV2.edit.sections.addOns', 'Add-Ons')}
              action={
                creating ? null : (
                  <Button
                    size="small"
                    onClick={() => openAddonManagement(editingItem)}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('menuV2.edit.manage', 'Manage')}
                  </Button>
                )
              }
            >
              {creating ? (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {t('menuV2.add.addonsHint', 'Save the item first, then manage its add-ons.')}
                </Typography>
              ) : (
                <AddOnsChecklist
                  addons={editAddons}
                  loading={editAddonsLoading}
                  currency={restaurantCurrency}
                />
              )}
            </DrawerSection>

            <DrawerSection title={t('menuV2.edit.sections.special', 'Special Instructions')}>
              <TextField
                placeholder={t('menuV2.edit.specialInstructionsPlaceholder', 'e.g. No onions, extra sauce')}
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 100 }}
                disabled
                helperText={t('menuV2.edit.specialInstructionsHint', 'Customers will fill this in at checkout.')}
              />
            </DrawerSection>
          </Box>
        )}
      </RightDrawer>

      {addonModalItem && (
        <AddonManagementModal
          item={addonModalItem}
          addons={addonsList}
          loading={addonsLoading}
          onClose={closeAddonManagement}
          onAddCategory={() => { setAddonFormEditing(null); setAddonFormOpen(true); }}
          onEdit={(addon) => { setAddonFormEditing(addon); setAddonFormOpen(true); }}
          onDelete={(addonId) => setAddonDeleteConfirmId(addonId)}
        />
      )}

      {addonModalItem && addonFormOpen && (
        <AddonForm
          menuItemId={addonModalItem.id}
          editAddon={addonFormEditing}
          onSave={handleSaveAddonCategory}
          onClose={() => { setAddonFormOpen(false); setAddonFormEditing(null); }}
        />
      )}

      <Dialog open={addonDeleteConfirmId != null} onClose={() => setAddonDeleteConfirmId(null)}>
        <DialogTitle>{t('admin.addons.deleteTitle', 'Delete add-on category?')}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {t('admin.addons.deleteMessage', 'This removes the category and all of its options.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddonDeleteConfirmId(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleDeleteAddonConfirm} color="error" variant="contained">
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameTarget != null} onClose={() => setRenameTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('admin.renameSection.title', 'Rename category')}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2, fontSize: 14 }}>
            {t('admin.renameSection.currentLabel', { name: renameTarget || '' })}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameCategoryConfirm();
              }
            }}
            placeholder={t('admin.renameSection.newNamePlaceholder', 'New category name')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)} disabled={categoryBusy}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleRenameCategoryConfirm} variant="contained" disabled={categoryBusy}>
            {categoryBusy ? t('common.saving', 'Saving...') : t('admin.sections.rename', 'Rename')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={translateTarget != null} onClose={() => setTranslateTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('menuV2.categories.translateTitle', 'Translate category')}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2, fontSize: 14 }}>
            {t('menuV2.categories.translateSubtitle', { name: translateTarget || '', defaultValue: 'English: {{name}}' })}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Español"
            value={dialogTrans.es.name}
            onChange={(e) => updateDialogTrans('es', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="العربية"
            value={dialogTrans.ar.name}
            onChange={(e) => updateDialogTrans('ar', e.target.value)}
            inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={handleSectionAutoTranslate}
            disabled={translateTranslating || translateBusy}
            sx={{ mt: 2, textTransform: 'none' }}
          >
            {translateTranslating
              ? t('translation.translating', 'Translating...')
              : `🔄 ${t('translation.autoTranslateOthers', 'Auto-translate others')}`}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranslateTarget(null)} disabled={translateBusy}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleSaveSectionTranslations} variant="contained" disabled={translateBusy}>
            {translateBusy ? t('common.saving', 'Saving...') : t('menuV2.edit.save', 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryDeleteName != null} onClose={() => setCategoryDeleteName(null)}>
        <DialogTitle>{t('admin.deleteSection.title', 'Delete category?')}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {t('admin.deleteSection.message', { name: categoryDeleteName || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDeleteName(null)} disabled={categoryBusy}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleDeleteCategoryConfirm} color="error" variant="contained" disabled={categoryBusy}>
            {t('admin.deleteSection.confirmButton', 'Delete category')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmId != null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>{t('admin.deleteMenuItem.title', 'Delete menu item?')}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {t('admin.deleteMenuItem.message', 'This permanently removes the item from your menu.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
};

export default AdminMenuV2;
